import type { DateRangeFns } from '@cobliteam/common-types';
import {
  filterGetParamDates,
  filterGetParamFilters,
  filterSetParamDates,
  filterSetParamFilters,
  type IFiltersDef,
  type SortingState,
  type TFilterKeys,
} from '@cobliteam/ui-business/components/DataTable';
import { TripImageHistoryTableType } from '@cobliteam/ui-business/utils/mediaUtils';
import { snakeCase } from 'change-case';
import type { StateCreator } from 'zustand';

type Sort = { key: string; desc: 'asc' | 'desc' } | undefined;

export interface TripImageHistorySliceState {
  tripId: string | undefined;
  tripImageNominateModal: boolean;
  imageIndex: number;
  tripImageHistoryDates: DateRangeFns | null;
  tripImageHistoryFilters: IFiltersDef[];
  tripImageHistorySort: Sort;
  tripImageHistoryTab: TripImageHistoryTableType;
  setTripId: (id: string | undefined) => void;
  setTripImageNominateModal: (isOpen: boolean) => void;
  setImageIndex: (index: number) => void;
  setTripImageHistoryDates: (dates: DateRangeFns) => void;
  setTripImageHistoryFilters: (filter: IFiltersDef) => void;
  setTripImageHistorySort: (sorts: SortingState) => void;
  getFilterValueByKey: <T>(key: TFilterKeys) => T | undefined;
  setTripImageHistoryTab: (tab: TripImageHistoryTableType) => void;
  clearTripImageHistoryFilters: () => void;
}

type StateCreatorTripImageHistoryTable = StateCreator<TripImageHistorySliceState, [['zustand/devtools', never]], [], TripImageHistorySliceState>;

const tripImageTableSlice: StateCreatorTripImageHistoryTable = (set, get) => ({
  tripId: undefined,
  tripImageNominateModal: false,
  imageIndex: 0,
  tripImageHistoryDates: filterGetParamDates(0),
  tripImageHistoryFilters: filterGetParamFilters(),
  tripImageHistorySort: undefined,
  tripImageHistoryTab: TripImageHistoryTableType.PENDING_NOMINATE_TRIPS,
  setTripId: (id: string | undefined) =>
    set(() => ({
      tripId: id,
    })),
  setTripImageNominateModal: (isOpen: boolean) =>
    set(() => ({
      tripImageNominateModal: isOpen,
    })),
  setImageIndex: (index: number) =>
    set(() => ({
      imageIndex: index,
    })),
  setTripImageHistoryDates: (dates: DateRangeFns) =>
    set(
      (): Partial<TripImageHistorySliceState> => {
        filterSetParamDates(dates);
        return { tripImageHistoryDates: dates };
      },
      false,
      'setDates'
    ),
  setTripImageHistoryFilters: (filter: IFiltersDef) =>
    set(
      (state): Partial<TripImageHistorySliceState> => {
        const filterDef = state.tripImageHistoryFilters.find((f: IFiltersDef) => f.key === filter.key) as IFiltersDef;
        const newFilter = {
          ...filterDef,
          ...filter,
        };
        filterSetParamFilters(newFilter);

        // create a copy of the filters array
        const updatedFilters = [...state.tripImageHistoryFilters];

        // find the filter with the matching key and update its value
        const filterIndex = updatedFilters.findIndex((f) => f.key === filter.key);

        if (filterIndex !== -1) {
          updatedFilters[filterIndex].value = filter.value;
        }

        return { tripImageHistoryFilters: updatedFilters };
      },
      false,
      'setFilters'
    ),
  setTripImageHistorySort: (sorts: SortingState) =>
    set(
      (): Partial<TripImageHistorySliceState> => {
        const newSort = sorts.length
          ? {
              key: snakeCase(sorts[0].id),
              desc: sorts[0].desc ? 'desc' : 'asc',
            }
          : undefined;

        return {
          tripImageHistorySort: newSort as Sort,
        };
      },
      false,
      'setSort'
    ),
  getFilterValueByKey: (key: TFilterKeys) => get().tripImageHistoryFilters.find((f: IFiltersDef) => f.key === key)?.value,
  setTripImageHistoryTab: (tab: TripImageHistoryTableType) =>
    set(() => ({
      tripImageHistoryTab: tab,
    })),
  clearTripImageHistoryFilters: () =>
    set(
      (state): Partial<TripImageHistorySliceState> => {
        const filtersToClearKeys = ['MultiSelectVehicles', 'MultiSelectVehicleGroups', 'MultiSelectFaceIdIdentificationMethods'];

        const updatedFilters = [...state.tripImageHistoryFilters];

        filtersToClearKeys.forEach((key) => {
          const filterIndex = updatedFilters.findIndex((f) => f.key === key);

          if (filterIndex !== -1 && updatedFilters[filterIndex].value.length > 0) {
            updatedFilters[filterIndex].value = [];

            filterSetParamFilters(updatedFilters[filterIndex]);
          }
        });

        return { tripImageHistoryFilters: updatedFilters };
      },
      false,
      'clearSelectedFilters'
    ),
});

export default tripImageTableSlice;
